/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { useEffect, FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Theme,
  Slide,
  Paper,
  Box,
  FormHelperText,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { styled } from '@material-ui/styles';
import useContentMgmt from '../../../hooks/useContentMgmt';
import { TransitionProps } from '@material-ui/core/transitions';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { useIsMobile } from '../../../util/style-utils';
import useUtil from '../../../hooks/pge-plus/useUtil';
import {
  ServiceProviderUserRole,
  UpdateServiceProviderUserInput,
} from '../../../__generated__/pgeplus-types';
import { useInstallerManageUser } from '../../../hooks/pge-plus/useInstallerManageUser';
import TextField from '../../text-field/TextField';
import DatePicker from '../../date-picker';
import PhoneTextField from '../../phone-text-field';
import EmailTextField from '../../email-text-field';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useFormState from '../../../hooks/useFormState';
import addUserFormValidationRules, { AddUserModal } from './AddUserForm.rules';
import PGEButton from '../../buttons/PGE-Button';
import theme from '../../../themes/theme';

const PREFIX = 'addUser';
const classes = {
  dialogPaper: `${PREFIX}-dialog-paper`,
  dialogContent: `${PREFIX}-dialog-content`,
  dialogTitle: `${PREFIX}-dialog-title`,
  dialogActions: `${PREFIX}-dialog-actions`,
  mainBox: `${PREFIX}-main-box`,
  box: `${PREFIX}-box`,
  Heading: `${PREFIX}-Heading`,
  hr: `${PREFIX}-hr`,
  footer: `${PREFIX}-footer`,
  permission: `${PREFIX}-permission`,
  editor: `${PREFIX}-editor`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const StyledDialog = styled(Dialog)(() => ({
  [`& .${classes.dialogPaper}`]: {
    maxWidth: '760px',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '600px !important',
      width: '100%',
      position: 'fixed',
      bottom: 0,
      left: 0,
    },
  },
  [`& .${classes.dialogContent}`]: {
    padding: '0 30px',
  },
  [`& .${classes.dialogTitle}`]: {
    padding: '30px 30px 0',
  },
  [`& .${classes.dialogActions}`]: {
    padding: '30px 30px 50px',
  },
  [`& .${classes.mainBox}`]: {
    justifyContent: 'center',
    marginTop: '20px',
  },
  [`& .${classes.box}`]: {
    backGroundColor: '#fff',
    padding: '20px',
  },
  [`& .${classes.Heading}`]: {
    letterSpacing: '0.72px',
    fontWeight: 700,
    fontSize: '36px',
    marginBottom: '16px',
  },
  [`& .${classes.hr}`]: {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.footer}`]: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  [`& .${classes.permission}`]: {
    fontSize: '24px',
    fontWeight: 700,
  },
  [`& .${classes.editor}`]: {
    fontSize: '16px',
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children?: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  isVisibleAdd?: any;
  selectedData?: any;
  onSuccess: () => void;
  onClose: () => void;
}

const DATE_FORMAT = 'yyyy-MM-dd';

const defaultFormState: AddUserModal = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  phone: '',
  email: '',
};

export const AddUserModel: FC<Props> = ({
  isVisibleAdd,
  onSuccess,
  onClose,
  selectedData,
}: Props) => {
  const isMobile = useIsMobile();
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_ADD_USER_SCREEN,
  );
  const { wrapWithLoader } = useWrapWithLoader();
  const { addUserMutation, updateUserMutation } = useInstallerManageUser();
  const [error, setError] = useState('');
  const { setErrorNotification } = useUtil();

  const addUserForm = useFormState(defaultFormState, {
    validate: addUserFormValidationRules,
    validationContext: {
      content,
    },
  });

  useEffect(() => {
    if (selectedData?.id) {
      void addUserForm.setValue('firstName', selectedData?.firstName);
      void addUserForm.setValue('email', selectedData?.email);
      void addUserForm.setValue('lastName', selectedData?.lastName);
      void addUserForm.setValue('dateOfBirth', selectedData?.birthDate);
      void addUserForm.setValue(
        'phone',
        selectedData?.phoneNumber
          ?.replace('(', '')
          .replace(')', '')
          .replace('-', '')
          .replace(' ', ''),
      );
    } else {
      void addUserForm.reset();
    }
  }, [selectedData?.id, isVisibleAdd]);

  const handleClose = async () => {
    void addUserForm.reset();
    onClose();
  };

  const handelSubmit = wrapWithLoader(async () => {
    if (selectedData?.id) {
      const inputParams = {
        id: selectedData?.id,
        phoneNumber: addUserForm?.values?.phone || '',
      } as UpdateServiceProviderUserInput;
      if (selectedData?.email !== addUserForm?.values?.email) {
        inputParams.email = addUserForm?.values?.email;
      }
      const { data, errors } = await updateUserMutation({
        variables: {
          input: inputParams,
        },
      });
      if (errors) {
        setErrorNotification(true);
      }
      if (data?.updateServiceProviderUser?.errorReason) {
        //setErrorNotification(true, `${data?.updateServiceProviderUser?.errorReason?.message}`, 'error');
        setError(
          data?.updateServiceProviderUser?.errorReason?.message ||
            content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE')!,
        );
        return;
      } else {
        setErrorNotification(
          true,
          content.get('YOUR_INFO_HAS_BEEN_UPDATED'),
          'success',
        );
        onSuccess();
        void addUserForm.reset();
      }
    } else {
      const inputParams = {
        birthDate: addUserForm?.values?.dateOfBirth,
        email: addUserForm?.values?.email || '',
        firstName: addUserForm?.values?.firstName || '',
        lastName: addUserForm?.values?.lastName || '',
        phoneNumber: addUserForm?.values?.phone || '',
        role: ServiceProviderUserRole?.Editor,
      };
      const { data, errors } = await addUserMutation({
        variables: {
          input: inputParams,
        },
      });
      if (data?.addServiceProviderUser?.errorReason) {
        //setErrorNotification(true, `${data?.addServiceProviderUser?.errorReason?.message}`, 'error');
        setError(
          data?.addServiceProviderUser?.errorReason?.message ||
            content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE')!,
        );
        return;
      }
      if (errors) {
        setErrorNotification(true);
      }
      if (data?.addServiceProviderUser?.user?.id) {
        setErrorNotification(
          true,
          content.get('A_NEW_USER_HAS_BEEN_INVITED'),
          'success',
        );
        onSuccess();
        void addUserForm.reset();
      }
    }
  });

  return (
    <StyledDialog
      open={isVisibleAdd}
      TransitionComponent={isMobile ? Transition : undefined}
      onClose={handleClose}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      scroll="body"
      fullWidth={true}
      disableRestoreFocus={true}
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Grid
          className={'outageTitle'}
          container
          css={{
            justifyContent: 'end',
          }}
        >
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={handleClose}
            css={{
              padding: 0,
            }}
          >
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <form
          onSubmit={e => {
            void handelSubmit();
            e.preventDefault();
          }}
        >
          <Grid>
            <Grid
              style={{
                marginTop: '20px',
                padding: '20px',
                background: '#EDF5FA',
              }}
            >
              <Typography className={classes.Heading}>
                {selectedData?.id
                  ? content.get('EDIT_INFORMATION_TITLE')
                  : content.get('ADD_NEW_USER_TITLE')}
              </Typography>
              <Paper className={classes.box}>
                <Typography>
                  {selectedData?.id
                    ? content.get('EDIT_USER_DETAILS')
                    : content.get('COMPLETE_THE_FOLLOWING_INFO_DETAILS')}
                </Typography>
                <Grid item xs={12} md={12} style={{ marginTop: '2em' }}>
                  <TextField
                    name={'firstName'}
                    id="nameUpdateInfo"
                    label={content.get('FIRST_NAME')}
                    style={{ width: '100%' }}
                    inputProps={{ maxLength: 90 }}
                    {...addUserForm?.props('firstName')}
                    disabled={!!selectedData?.id}
                  />
                </Grid>
                <Grid item xs={12} md={12} style={{ marginTop: '2em' }}>
                  <TextField
                    name={'lastName'}
                    id="nameUpdateInfo"
                    label={content.get('LAST_NAME')}
                    style={{ width: '100%' }}
                    inputProps={{ maxLength: 90 }}
                    {...addUserForm?.props('lastName')}
                    disabled={!!selectedData?.id}
                  />
                </Grid>
                <Grid item xs={12} md={12} style={{ marginTop: '2em' }}>
                  <DatePicker
                    data-testid="startDate"
                    style={{ width: '100%' }}
                    label={content.get('DATE_OF_BIRTH')}
                    placeholder="YYYY-MM-DD"
                    name="dateOfBirth"
                    defaultValue={null}
                    mask={'____-__-__'}
                    labelShrink={{ shrink: true }}
                    format={DATE_FORMAT}
                    {...addUserForm?.props('dateOfBirth')}
                    error={Boolean(addUserForm?.errors.dateOfBirth)}
                    helperText={addUserForm?.errors.dateOfBirth}
                    disabled={!!selectedData?.id}
                  />
                </Grid>
                <Grid item xs={12} md={12} style={{ marginTop: '1em' }}>
                  <PhoneTextField
                    name="phone"
                    style={{ width: '100%', background: 'transparent' }}
                    {...addUserForm?.props('phone')}
                  />
                </Grid>
                <Grid item xs={12} md={12} style={{ marginTop: '0.5em' }}>
                  <EmailTextField
                    label={content.get('REUSABLE_EMAIL')}
                    style={{ width: '100%' }}
                    name="email"
                    {...addUserForm?.props('email')}
                    error={Boolean(addUserForm.errors.email)}
                  />
                </Grid>
                <hr className={classes.hr} />
                <Box className={classes.footer}>
                  <Typography className={classes.permission}>
                    {content.get('PERMISSIONS')}
                  </Typography>
                  <Typography className={classes.editor}>
                    {selectedData?.role === ServiceProviderUserRole.Admin
                      ? content.get('ADMIN')
                      : content.get('EDITOR')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          <Box>
            {error && (
              <FormHelperText
                error
                css={{
                  letterSpacing: '1px',
                  marginTop: theme.spacing(2),
                  fontSize: theme.spacing(2),
                  textAlign: 'center',
                }}
              >
                {error}
              </FormHelperText>
            )}
          </Box>

          <Grid xs={12} css={{ padding: '15px' }}>
            <Grid container spacing={2}>
              <Grid xs={2} />
              <Grid xs={8}>
                <Grid
                  container
                  spacing={2}
                  css={{
                    marginTop: 16,
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Grid item xs={6}>
                    <PGEButton
                      onClick={handleClose}
                      type="button"
                      fullWidth
                      variant="outlined"
                      color="secondary"
                      css={{
                        fontSize: '18px',
                        borderRadius: '30px',
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                        '&:hover': {
                          borderWidth: '2px',
                        },
                      }}
                    >
                      {content.get('REUSABLE_CANCEL')}
                    </PGEButton>
                  </Grid>
                  <Grid item xs={6}>
                    <PGEButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      css={{
                        fontSize: '18px',
                        borderRadius: '30px',
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                      }}
                      disabled={
                        addUserForm.values?.firstName === '' &&
                        addUserForm.values?.lastName === '' &&
                        addUserForm.values?.phone === '' &&
                        addUserForm.values?.email === '' &&
                        addUserForm.values?.dateOfBirth === ''
                      }
                    >
                      {selectedData?.id ? 'Save' : content.get('ADD_USER')}
                    </PGEButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid xs={2} />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};
